
import Component from "vue-class-component"
import PageHeader from "@/superannuation/PageHeader.vue"
import HelpIcon from "@/ui/HelpIcon.vue"
import Vue from "vue"
import SelectDropDown from "@/components/SelectDropDown.vue"
import {QPopupProxy} from "quasar"
import Axios, {AxiosError, AxiosResponse} from "axios"
import {Member} from "@/superannuation/PlanService"
import ErrorMessage from "@/components/ErrorMessage.vue"
import {format, parse} from "date-fns"
import {Inject} from "vue-property-decorator"
import {AuthenticationService, UserRole} from "@/AuthenticationService"
import {getIsoDate} from "@/utils/formatter"
import _ from "lodash"

import {MemberEvent, MemberEventRequest} from "./MemberEvents"

@Component({
  components: {
    ErrorMessage,
    SelectDropDown,
    PageHeader,
    HelpIcon,
    QPopupProxy,
  },
})
export default class MemberEventsView extends Vue {
  @Inject()
  authenticationService: AuthenticationService

  $refs: {
    userSelect: SelectDropDown
    memberSelect: SelectDropDown
    qAtDateProxy: QPopupProxy
  }

  events: Array<MemberEvent> = new Array<MemberEvent>()
  createEventsRequest = new MemberEventRequest()

  user: User | null = null
  member: Member | null = null
  memberId: string | null = null
  atDate: string | null = null

  message: string | null = null
  dataInProgress = false
  isStaff = false

  dateRule = (v: string | null) =>
    v != null &&
    /^([0-2]\d|(3)[01])(\/)(((0)\d)|((1)[0-2]))(\/)\d{4}$/.test(v) &&
    parse(v, "dd/MM/yyyy", new Date()) <= new Date()

  dateOptions = (v: string) => v <= format(Date.now(), "yyyy/MM/dd")

  async created() {
    this.isStaff = await this.authenticationService.isStaff()
    this.memberId = <string | null>this.$route.query.memberId
    if (this.isReadOnly) await this.getEvents()
    else await this.reset()
  }

  get isSubmitDisabled(): boolean {
    return (
      this.member == null ||
      !this.dateRule(this.atDate) ||
      this.user == null ||
      this.createEventsRequest.isCreateNone()
    )
  }

  get isRiskProfilerDisabled(): boolean {
    return this.user == null || this.member == null
  }

  get riskProfilerLink() {
    return this.isRiskProfilerDisabled
      ? ""
      : `/ssn-risk-profiler/?memberId=${this.member!.id}&consultant=${this.user!.name}`
  }

  get isReadOnly(): boolean {
    return this.memberId != null || !this.isStaff
  }

  private isDisplayProperty(key: string): boolean {
    return !["eventType", "at", "processed", "id"].includes(key)
  }

  private static readonly eventColorByType: Record<string, string | undefined> = {
    BeneficiaryNominationValidated: "green-7",
    PotentialConsolidationVerified: "green-7",
    ConsultationPerformed: "blue-9",
    AdviceDownloaded: "blue-9",
    ReportDownloaded: "blue-9",
    RiskProfileAssessmentCompleted: "orange-8",
    InsuranceAssessmentCompleted: "orange-8",
    ProjectionModellerCompleted: "orange-8",
    ContributionCalculatorCompleted: "orange-8",
    TtrCalculatorCompleted: "orange-8",
  }

  private getEventColor(eventType: string): string {
    return MemberEventsView.eventColorByType[eventType] ?? "grey"
  }

  private getIconByType(): Record<string, string | undefined> {
    return {
      ConsultationPerformed: this.$icons.siBubbles,
      RiskProfileAssessmentCompleted: this.$icons.mdiPencil,
      InsuranceAssessmentCompleted: this.$icons.siCalculator,
      ProjectionModellerCompleted: this.$icons.siCalculator,
      ContributionCalculatorCompleted: this.$icons.siCalculator,
      TtrCalculatorCompleted: this.$icons.siCalculator,
      EmailSubscriptionChanged: this.$icons.mdiMinus,
      ContactRequested: this.$icons.siPhone,
      AdviceDownloaded: this.$icons.mdiDownload,
      ReportDownloaded: this.$icons.mdiDownload,
    }
  }

  private getIcon(eventType: string): string {
    return this.getIconByType()[eventType] ?? this.$icons.mdiCheck
  }

  private userSelected(user: User | null) {
    this.user = user
  }

  private async memberSelected(member: Member | null) {
    this.member = member
    if (member === null) {
      this.events = new Array<MemberEvent>()
    } else {
      await this.getEvents()
    }
  }

  private memberLabel(): (option: Member) => string {
    return (option: Member) => `${option.surname} [${option.login}] - ${option.companyName}`
  }

  private async getEvents() {
    this.dataInProgress = true
    const memberId = _.isNil(this.memberId) ? this.member!.id : this.memberId
    await Axios.get(`/api/members/${memberId}/events`)
      .then((response: AxiosResponse) => {
        this.message = null
        this.events = response.data
      })
      .catch((reason: AxiosError<any>) => {
        this.message = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  private async deleteEvent(eventId: string) {
    await Axios.delete(`/api/members/events/${eventId}`)
      .then(() => {
        this.message = null
        void this.getEvents()
      })
      .catch((reason: AxiosError<any>) => {
        this.message = reason.response?.data.message
      })
  }

  private async createEvents() {
    this.dataInProgress = true

    this.createEventsRequest.effectiveDate = getIsoDate(this.atDate)!
    this.createEventsRequest.consultant = this.user!.name
    this.createEventsRequest.memberId = this.member!.id

    await Axios.post(`/api/members/events`, this.createEventsRequest)
      .then((response: AxiosResponse) => {
        this.message = null
        console.log("Created Member Event/s", response.data)
        void this.getEvents()
      })
      .catch((reason: AxiosError<any>) => {
        this.message = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  private async reset() {
    this.atDate = format(Date.now(), "dd/MM/yyyy")

    const current: User = await this.authenticationService.loadCurrentUser()
    this.$refs.userSelect.model = null
    this.user = current.role === UserRole[UserRole.AuthorizedRep] ? current : null

    this.$refs.memberSelect.model = null
    this.member = null

    this.createEventsRequest.reset()
  }
}
